import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { Avatar } from '@/components/console/Avatar';
import { useT } from '@/i18n/labels';
import { useAuthStore } from '@/stores/authStore';
import { useConversationStore } from '@/stores/conversationStore';
import { usePermission } from '@/hooks/usePermission';
import { useFolderCounts } from '@/console/useFolderCounts';
import type { LabelKey } from '@/i18n/labels';

/** Tiny BotableX-inspired mark (chat-bubble gradient). */
function BotableXMark({ size = 12 }: { size?: number }) {
  return <span className="ac-bx-dot" style={{ width: size, height: size }} aria-label="BotableX" />;
}

interface NavItem {
  id: string;
  icon: string;
  labelKey: 'nav_myday' | 'nav_conversations' | 'nav_queue' | 'nav_team' | 'nav_spam';
  path: string;
  count?: number;
  consult?: number;
  /**
   * OP-582 — conversations holding an email reply draft that is waiting for a human.
   * Rendered ALONGSIDE the other badges rather than competing with them: an agent sitting on
   * My Day or Team would otherwise have no signal at all that a customer is unanswered,
   * which is how a held draft could sit its whole review window unseen.
   */
  drafts?: number;
}

/**
 * The label under the signed-in agent's name: what their token's roles make them. Admin (or platform admin) over senior
 * agent over agent; an unrecognised role set reads as a plain agent (the lowest label, never a higher one). UX only.
 */
export function roleLabelKey(roles: readonly string[]): LabelKey {
  if (roles.includes('admin') || roles.includes('platformAdmin')) return 'role_admin';
  if (roles.includes('seniorAgent')) return 'role_senior_agent';
  return 'role_agent';
}

/** Left navigation rail: brand, primary nav, and the current-agent footer. */
export const Rail: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const { hasPermission, roles } = usePermission();
  const { countOf } = useFolderCounts();

  // Collapsed (icons only) unless this agent expanded it before (operator 2026-10-12: the left side took too much room;
  // the folder panel already names every place, so the icon rail only needs to be wide on request).
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('ac-rail-collapsed') !== '0';
    } catch {
      return true;
    }
  });
  const toggle = () =>
    setCollapsed((c) => {
      const n = !c;
      try {
        localStorage.setItem('ac-rail-collapsed', n ? '1' : '0');
      } catch {
        /* ignore */
      }
      return n;
    });

  // ADR-033: the rail's numbers are the SERVER's folder counts, not a tally of the rows this console happens to hold (a
  // folder is paged, so the rows in memory are a lower bound). The "Conversations" badge is what is MINE - it used to be
  // every open conversation in the tenant - and the consultation badge is what still needs a human. The drafts badge is
  // the drafts folder (OP-582). `null` = the counts have not arrived yet: no badge rather than a false zero.
  const activeCount = countOf('mine') ?? 0;
  // The counts endpoint no longer reports a `need-consult` folder: "needs help" is the lifecycle tab, counted by the loaders.
  const consultCount = useConversationStore((s) => s.needHelpCount) ?? 0;
  const draftCount = countOf('drafts') ?? 0;

  const items: NavItem[] = [
    { id: 'myday', icon: 'myday', labelKey: 'nav_myday', path: '/' },
    {
      id: 'conversations',
      icon: 'chat',
      labelKey: 'nav_conversations',
      path: '/conversations',
      count: activeCount,
      consult: consultCount,
      drafts: draftCount,
    },
    { id: 'queue', icon: 'queue', labelKey: 'nav_queue', path: '/queue' },
    { id: 'team', icon: 'team', labelKey: 'nav_team', path: '/team' },
    // OP-1005 — the Undo view for conversations marked as spam. Offered only to a user holding
    // workspace.handle, the permission the server requires on the list and on Undo.
    ...(hasPermission('workspace.handle')
      ? [{ id: 'spam', icon: 'ban', labelKey: 'nav_spam' as const, path: '/spam' }]
      : []),
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const collapseLabel = collapsed ? t('railExpand') : t('railCollapse');
  // OP-367 — prefer the human-readable tenant name; fall back to the tenantId only when the
  // token predates the tenantName claim (older sessions), then to the brand.
  const tenantName = (user?.tenantName || user?.tenantId || 'BotableX').trim();
  const tenantInitial = (tenantName.match(/[a-z0-9]/i)?.[0] || 'B').toUpperCase();
  const settingsActive = location.pathname.startsWith('/settings');

  return (
    <nav className={'ac-rail' + (collapsed ? ' collapsed' : '')}>
      <div className="ac-rail-brand">
        <div className="ac-rail-tenant">{tenantInitial}</div>
        <div className="ac-rail-brand-text">
          <div className="ac-rail-tenant-name" title={tenantName}>
            {tenantName}
          </div>
          <div className="ac-rail-tenant-sub">
            <BotableXMark /> {t('poweredBy')}
          </div>
        </div>
        <button
          className="ac-rail-toggle"
          onClick={toggle}
          title={collapseLabel}
          aria-label={collapseLabel}
          data-testid="rail-toggle"
        >
          <Icon name="chevron" size={16} className="ac-rail-toggle-chev ac-flip" />
        </button>
      </div>

      <div className="ac-nav-label">{t('navSectionWorkspace')}</div>
      <div className="ac-nav">
        {items.map((it) => (
          <button
            key={it.id}
            className={'ac-nav-item' + (isActive(it.path) ? ' active' : '')}
            onClick={() => navigate(it.path)}
            title={collapsed ? t(it.labelKey) : undefined}
            data-testid={`nav-${it.id}`}
            aria-current={isActive(it.path) ? 'page' : undefined}
          >
            <Icon name={it.icon} size={19} stroke={isActive(it.path) ? 2.2 : 1.9} />
            <span className="ac-nav-item-label">{t(it.labelKey)}</span>
            {it.drafts && it.drafts > 0 ? (
              <span
                className="ac-nav-count draft"
                data-testid={`nav-${it.id}-drafts`}
                title={t('draft_queue_title')}
              >
                {it.drafts}
              </span>
            ) : null}
            {it.consult && it.consult > 0 ? (
              <span className="ac-nav-count consult" data-testid={`nav-${it.id}-consult`}>
                {it.consult}
              </span>
            ) : it.count && it.count > 0 ? (
              <span className="ac-nav-count" data-testid={`nav-${it.id}-count`}>
                {it.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="ac-rail-foot">
        <div
          className={'ac-rail-me' + (settingsActive ? ' active' : '')}
          onClick={() => navigate('/settings')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') navigate('/settings');
          }}
          title={t('nav_settings')}
          data-testid="rail-settings"
        >
          <Avatar name={user?.displayName} seed={user?.agentId} size={34} />
          <div className="ac-rail-me-text" style={{ flex: 1, minWidth: 0 }}>
            <div className="ac-rail-me-name">
              {user?.displayName || t('role_agent')}
            </div>
            <div className="ac-rail-me-role" data-testid="rail-me-role">
              {t(roleLabelKey(roles))}
            </div>
          </div>
          <Icon name="cog" size={16} className="ac-rail-me-cog" style={{ opacity: 0.7 }} />
        </div>
      </div>
    </nav>
  );
};
